"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-hot-toast";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged, verifyBeforeUpdateEmail } from "firebase/auth";
import { CreditCard, User, Info } from "lucide-react";
import isEducationalEmail from "../hooks/isEducationalDiscount.js";
// import { Button } from "@/components/ui/button";
import MarketingNavbar from "../components/marketingNav";
import LoadingSpinner from "../components/loadingSpinner";
import { auth, getUserPlanData, db, functions } from "../services/firebase";
import { STRIPE_CONFIG } from "../config/stripe";
import ManageSubscriptionButton from "../components/elements/manageSubscription";
import SubscriptionButton from "../components/elements/subscriptionButton.tsx";
import UpgradeModal from "../components/elements/upgradeModal.tsx";

export default function MyAccount() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userPlanInfo, setUserPlanInfo] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [pastDue, setPastDue] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [activeBilling, setActiveBilling] = useState("monthly");
  const [isEducational, setIsEducational] = useState(false);
  const drills = 2;
  const storageLimit = 5;

  const usagePercentage = (drills / storageLimit) * 100;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "United States",
    streetAddress: "",
    city: "",
    region: "",
    postalCode: "",
    plan: "base",
    planActive: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserData();
      } else {
        toast.error("No user found, please log in");
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "users"),
        where("uid", "==", auth.currentUser?.uid)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = { id: userDoc.id, ...userDoc.data() };
        setUserData(userData);
        setFormData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
          country: userData.country || "United States",
          streetAddress: userData.streetAddress || "",
          city: userData.city || "",
          region: userData.region || "",
          postalCode: userData.postalCode || "",
          plan: userData.plan || "base",
          planActive: userData.planActive || false,
        });
        const planData = await getUserPlanData(auth.currentUser.uid);
        const isEducational = isEducationalEmail(userData.email);
        if (planData.planKey === "past_due") {
          setPastDue(true);
        }
        setIsEducational(isEducational);
        setUserPlanInfo(planData);
      } else {
        console.error("No user data found for the current UID");
      }
    } catch (error) {
      console.error("Error fetching user data: ", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const planDataBack = await getUserPlanData(user.uid);
        setPlanData(planDataBack);
      } else {
        toast.error("No user found, please log in");
        navigate("/login");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No user found");
      }

      // Check if email has changed
      if (formData.email !== user.email) {
        try {
          // Send verification email before updating
          await verifyBeforeUpdateEmail(user, formData.email);
          toast.success("Verification email sent! Please check your new email address to confirm the change.");
          return; // Don't update Firestore until email is verified
        } catch (error) {
          if (error.code === 'auth/requires-recent-login') {
            toast.error("Please re-authenticate before changing your email. Try logging out and back in.");
            return;
          }
          throw error;
        }
      }

      // Only update Firestore if email hasn't changed
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: user.email, // Use current email since new one isn't verified yet
        country: formData.country,
        streetAddress: formData.streetAddress,
        city: formData.city,
        region: formData.region,
        postalCode: formData.postalCode,
        plan: formData.plan,
        planActive: formData.planActive,
      });

      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating user data: ", error);
      toast.error(error.message || "Failed to update profile");
    }
  };

  const getPortalLink = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      const createPortalLink = httpsCallable(
        functions,
        "ext-firestore-stripe-payments-ojuh-createPortalLink"
      );

      const { data } = await createPortalLink({
        returnUrl: window.location.origin,
      });

      if (!data?.url) {
        throw new Error("No portal URL received from server");
      }

      window.location.assign(data.url);
    } catch (error) {
      console.error("Portal link error:", error);
      toast.error(
        "Unable to access the billing portal. Please try again or contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <MarketingNavbar />
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>DrillFlo ✨ My Account</title>
      </Helmet>
      <MarketingNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">
            Settings
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            You can manage your account, billing, and team settings here.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Account info and tabs */}
          <div className="space-y-6">
            {/* Basic Information Card */}
            <div className="bg-white rounded-lg border shadow-sm p-6">
              <div className="pb-3 border-b mb-4">
                <h2 className="text-xl font-semibold">Basic Information</h2>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Name</p>
                    <p className="text-base">{`${formData.firstName} ${formData.lastName}`}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="h-6 w-6 text-gray-600" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="text-base">{formData.email}</p>
                </div>
              </div>
            </div>
            {/* Account Card */}
            <div className="bg-white rounded-lg border shadow-sm p-6">
              <div className="pb-3 border-b mb-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Account</h2>
                  {pastDue && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                      Past Due
                    </span>
                  )}
                  {planData?.planActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                      Pro
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                      Free
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                {planData?.planActive ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      You are currently on DrillFlo Pro.
                    </p>
                    <button
                      className={` block md:w-full h-fit flex m-auto justify-center items-center
                     border border-2 border-blue-400 text-black hover:bg-blue-200
                        transition-all ease-linear rounded-md py-2 text-sm font-semibold text-center`}
                      onClick={getPortalLink}
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      Manage Subscription
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Upgrade to unlock premium features and increase your usage
                      limits.
                    </p>
                    <div className="space-y-3">
                      <div className="flex justify-center space-x-4 mb-4">
                        <button
                          className={`px-4 py-2 rounded-md border text-sm font-medium ${activeBilling === "monthly"
                            ? "bg-blue-500 text-white"
                            : "bg-gray-100 text-gray-700"
                            }`}
                          onClick={() => setActiveBilling("monthly")}
                        >
                          Monthly ($25/month)
                        </button>
                        <button
                          className={`px-4 py-2 rounded-md border text-sm font-medium ${activeBilling === "annual"
                            ? "bg-blue-500 text-white"
                            : "bg-gray-100 text-gray-700"
                            }`}
                          onClick={() => setActiveBilling("annual")}
                        >
                          Annual ($250/year)
                        </button>
                      </div>

                      <SubscriptionButton
                        priceId={
                          activeBilling === "monthly"
                            ? STRIPE_CONFIG.prices.professional["monthly"]
                            : STRIPE_CONFIG.prices.professional["annual"]
                        }
                        userId={userData?.uid}
                        planName="Pro"
                        className="w-full"
                        trialLength={process.env.REACT_APP_DRILLFLO_PRO_TRIAL_LENGTH || 15}
                        alreadyTrialed={pastDue}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Usage Card */}
            <div className="bg-white rounded-lg border shadow-sm p-6 hidden">
              <div className="pb-3 border-b mb-4">
                <h2 className="text-xl font-semibold">Usage Statistics</h2>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mt-1">
                  Monitor your usage and subscription limits.
                </p>
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <h3 className="font-medium">Drill Designs </h3>
                          <Info className="h-4 w-4 text-gray-400 ml-1" />
                        </div>
                      </div>
                      <div className="text-sm font-medium">
                        {drills} / {storageLimit}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gray-600 h-2 rounded-full"
                        style={{ width: `${usagePercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs for different sections */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
              {/* <div className="border-b">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab("personal")}
                    className={`px-4 py-3 text-sm font-medium flex-1 text-center ${
                      activeTab === "personal"
                        ? "border-b-2 border-gray-900 text-gray-900"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Personal Info
                  </button>
                  <button
                    onClick={() => setActiveTab("billing")}
                    className={`px-4 py-3 text-sm font-medium flex-1 text-center ${
                      activeTab === "billing"
                        ? "border-b-2 border-gray-900 text-gray-900"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Billing
                  </button>
                  <button
                    onClick={() => setActiveTab("usage")}
                    className={`px-4 py-3 text-sm font-medium flex-1 text-center ${
                      activeTab === "usage"
                        ? "border-b-2 border-gray-900 text-gray-900"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Usage
                  </button>
                </div>
              </div> */}

              <div className="p-6">
                {/* Personal Information Tab */}
                {activeTab === "personal" && (
                  <div>
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold">
                        Personal Information
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Update your personal information and address details.
                      </p>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label
                            htmlFor="firstName"
                            className="block text-sm font-medium text-gray-700"
                          >
                            First name
                          </label>
                          <input
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="Enter your first name"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label
                            htmlFor="lastName"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Last name
                          </label>
                          <input
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Enter your last name"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email address
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                        />
                      </div>

                      {/* <div className="space-y-2">
                        <label
                          htmlFor="country"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Country
                        </label>
                        <select
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                        >
                          <option value="United States">United States</option>
                          <option value="Canada">Canada</option>
                          <option value="Mexico">Mexico</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="streetAddress"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Street address
                        </label>
                        <input
                          id="streetAddress"
                          name="streetAddress"
                          value={formData.streetAddress}
                          onChange={handleChange}
                          placeholder="Enter your street address"
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <label
                            htmlFor="city"
                            className="block text-sm font-medium text-gray-700"
                          >
                            City
                          </label>
                          <input
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="Enter your city"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label
                            htmlFor="region"
                            className="block text-sm font-medium text-gray-700"
                          >
                            State / Province
                          </label>
                          <input
                            id="region"
                            name="region"
                            value={formData.region}
                            onChange={handleChange}
                            placeholder="Enter your state"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label
                            htmlFor="postalCode"
                            className="block text-sm font-medium text-gray-700"
                          >
                            ZIP / Postal code
                          </label>
                          <input
                            id="postalCode"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleChange}
                            placeholder="Enter your ZIP code"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
                          />
                        </div>
                      </div> */}

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className={`mt-8 block md:w-2/5 
                           bg-blue-600 hover:bg-purple-600
                             transition-all ease-linear rounded-md py-2 text-sm font-semibold text-white text-center`}
                        >
                          Save Changes
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Billing Tab */}
                {activeTab === "billing" && (
                  <div>
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold">
                        Subscription Management
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Manage your subscription and billing information.
                      </p>
                    </div>
                    <div className="space-y-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-blue-800">
                              Current Plan
                            </h3>
                            <p className="mt-1 text-sm text-blue-700">
                              {planData?.planActive
                                ? "DrillFlo Pro"
                                : "Free Tier"}
                            </p>
                            {planData?.planActive && (
                              <p className="mt-1 text-xs text-blue-600">
                                Your next billing date is on May 15, 2025
                              </p>
                            )}
                          </div>
                          {planData?.planActive ? (
                            <ManageSubscriptionButton />
                          ) : (
                            <SubscriptionButton
                              priceId={
                                STRIPE_CONFIG.prices.professional["monthly"]
                              }
                              userId={userData?.uid}

                              planName="Pro"
                            />
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">Payment Methods</h3>
                        {planData?.planActive ? (
                          <div className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex items-center">
                              <div className="h-10 w-10 bg-gray-100 rounded-md flex items-center justify-center mr-3">
                                <CreditCard className="h-5 w-5 text-gray-600" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  Visa ending in 4242
                                </p>
                                <p className="text-sm text-gray-500">
                                  Expires 04/2026
                                </p>
                              </div>
                            </div>
                            <button className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1 rounded hover:bg-gray-100">
                              Edit
                            </button>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">
                            No payment methods available. Add a payment method
                            when you upgrade to a paid plan.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Usage Tab */}
                {activeTab === "usage" && (
                  <div>
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold">
                        Usage Statistics
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Monitor your usage and subscription limits.
                      </p>
                    </div>
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <h3 className="font-medium">Premium models</h3>
                              <Info className="h-4 w-4 text-gray-400 ml-1" />
                            </div>
                            <p className="text-sm text-gray-500">
                              You've used 14 requests out of your 500 monthly
                              fast requests quota.
                            </p>
                          </div>
                          <div className="text-sm font-medium">14 / 500</div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gray-600 h-2 rounded-full"
                            style={{ width: "2.8%" }}
                          ></div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center">
                              <h3 className="font-medium">Standard models</h3>
                              <Info className="h-4 w-4 text-gray-400 ml-1" />
                            </div>
                            <p className="text-sm text-gray-500">
                              You've used 0 requests of this model. You have no
                              monthly quota.
                            </p>
                          </div>
                          <div className="text-sm font-medium">
                            0 / No Limit
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gray-600 h-2 rounded-full"
                            style={{ width: "0%" }}
                          ></div>
                        </div>
                      </div>

                      <hr className="my-4" />

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <Info className="h-5 w-5 text-amber-600 mr-2" />
                          <div>
                            <p className="text-sm text-amber-800">
                              Usage-based pricing allows you to pay for extra
                              requests beyond your plan limits.
                            </p>
                            <a
                              href="#"
                              className="text-sm font-medium text-amber-800 underline mt-1 inline-block"
                            >
                              Learn more
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <h3 className="font-medium">
                              Enable usage-based pricing
                            </h3>
                            <Info className="h-4 w-4 text-gray-400 ml-1" />
                          </div>
                          <p className="text-sm text-gray-500">
                            Pay only for what you use beyond your plan limits
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
